/* ═════════════════════════ useAuth ══════════════════════════════════
   React hooks + capability helpers for the multi-user system.

   AuthGate puts the current `user` object on a React context; every
   component can read it via useAuth(), and gate UI with useCan(capability)
   or the static `can(user, capability)` helper.

   Roles:
     super-admin — workspace owner. Always has Team access. Can't be
                   removed or demoted directly; must transfer power to
                   another admin first.
     admin       — full ops; Team access only when explicitly granted
                   by a super-admin.
     roaster     — operational screens (orders, production, inventory,
                   cupping, products) — no settings/team/pricing.
     packer      — packing workflow only.
     cupper      — labs / cupping only.
     delivery    — shipping + routes only.
     inventory   — green inventory + receiving only.
   ═══════════════════════════════════════════════════════════════════ */
import { createContext, useContext, useMemo } from "react";

/* Capability → list of roles allowed. super-admin is implicit in every
   list that contains "admin" (treated as admin++). Don't hand-add it. */
export const CAPABILITY_TO_ROLES = {
  /* Top-level pages */
  "page:orders":      ["admin", "roaster", "packer", "delivery"],
  "page:production":  ["admin", "roaster", "packer"],
  "page:inventory":   ["admin", "roaster", "inventory"],
  "page:analytics":   ["admin"],
  "page:cupping":     ["admin", "roaster", "cupper"],
  "page:products":    ["admin", "roaster"],
  "page:settings":    ["admin", "roaster", "packer", "cupper", "delivery", "inventory"],
  "page:wholesale":   ["admin"],
  "page:shipping":    ["admin", "roaster", "delivery"],
  "page:purchasing":  ["admin", "roaster", "inventory"],

  /* Production-subpage gating */
  "prod:breakdown":   ["admin", "roaster"],
  "prod:activity":    ["admin", "roaster"],
  "prod:packing":     ["admin", "roaster", "packer"],

  /* Destructive / administrative actions */
  "settings:connectors":  ["admin"],
  "settings:team":        ["admin"],   // gated extra by teamAccess flag, see can()
  "settings:preferences": ["admin"],
  "settings:pricing":     ["admin"],
  "settings:profile":     ["admin", "roaster", "packer", "cupper", "delivery", "inventory"],

  "inventory:write":      ["admin", "roaster", "inventory"],
  "inventory:delete":     ["admin", "inventory"],
  "cupping:write":        ["admin", "roaster", "cupper"],
  "orders:schedule":      ["admin", "roaster"],
  "orders:mark-shipped":  ["admin", "roaster", "packer", "delivery"],
  "orders:skip":          ["admin", "roaster", "packer"],
  "shipping:label":       ["admin", "roaster", "delivery"],
  "shipping:routes":      ["admin", "roaster", "delivery"],
  "fsma:export":          ["admin"],
};

/* God-mode emails — the platform owner(s). Always pass every capability
   check, every module-ownership check, every workspace gate. Used for
   support / debugging across customer workspaces without having to
   pay-as-customer or fiddle with role assignments. */
export const PLATFORM_OWNERS = new Set([
  "j@elionlabs.org",
  "john@elionlabs.org",
]);

export function isPlatformOwner(user) {
  return !!(user?.email && PLATFORM_OWNERS.has(String(user.email).toLowerCase().trim()));
}

export function can(user, capability) {
  if (!user) return false;
  // Platform owners bypass every gate — same access regardless of which
  // workspace they're in or what role they hold there.
  if (isPlatformOwner(user)) return true;
  // super-admin has every cap any admin has, plus a couple of locked
  // ones. Treat them as admin++ so we don't have to hand-add them to
  // every list above.
  const role = user.role === "super-admin" ? "admin" : user.role;
  // Team-page access has an extra per-user gate: admins only get there
  // when a super-admin has flipped on `teamAccess`. The super-admin
  // role itself always passes.
  if (capability === "settings:team") {
    if (user.role === "super-admin") return true;
    if (user.role !== "admin") return false;
    return !!user.teamAccess;
  }
  const allowed = CAPABILITY_TO_ROLES[capability];
  if (!allowed) return true; // unknown cap = allow (don't break the UI)
  return allowed.includes(role);
}

const AuthContext = createContext({ user: null, refresh: () => {}, logout: () => {} });

export const AuthProvider = AuthContext.Provider;

export function useAuth() {
  return useContext(AuthContext);
}

export function useCan(capability) {
  const { user } = useAuth();
  return useMemo(() => can(user, capability), [user, capability]);
}

export const ROLE_LABELS = {
  "super-admin": "Super-admin",
  admin:         "Admin",
  roaster:       "Roaster",
  packer:        "Packer",
  cupper:        "Cupper",
  delivery:      "Delivery",
  inventory:     "Inventory",
};

export const ROLE_DESCRIPTIONS = {
  "super-admin": "Workspace owner. Full access. Can't be removed without passing power to another admin.",
  admin:         "Full ops. Team page is locked unless the super-admin grants access.",
  roaster:       "Production, inventory, cupping, products. No settings or team.",
  packer:        "Packing workflow only — Ready/Produced/Skipped orders + packing list.",
  cupper:        "Labs only — cupping sessions, samples, scoring.",
  delivery:      "Shipping only — labels, routes, mark-shipped.",
  inventory:     "Inventory only — lots, receiving, allocations.",
};

/* Roles you can pick when inviting from the Team page. Excludes
   super-admin (assigned only via transfer). */
export const INVITABLE_ROLES = ["admin", "roaster", "packer", "cupper", "delivery", "inventory"];

/* Profile tags for non-team-member access — e.g. cupping-invite guests
   that scored a sample on their phone but aren't part of the workspace
   user list. Doesn't count against plan user limits. */
export const GUEST_PROFILE_LABELS = {
  cupping: "Cupping Guest",
};
